import { ApiError } from '@floorfile/sdk'
import { getActiveLocale, getCopy, translateErrorCode, useCopy } from '@floorfile/i18n'
import { errorCopy } from './copy'

/**
 * One place that turns a thrown value into a sentence a person can act on.
 *
 * Two entry points because failures surface in two worlds: React (a form, a
 * detail page) and not-React (the query client's `MutationCache`, a `lib/`
 * helper). They resolve identically — `getCopy` is the same function `useCopy`
 * calls, and `getActiveLocale()` is the locale `I18nProvider` mirrored out of
 * React for exactly this.
 */

/**
 * An envelope error whichever transport threw it. The SDK throws `ApiError`;
 * `@floorfile/offline`'s direct path throws a plain `Error` shaped like one
 * (`envelopeError` — it cannot import the SDK). Both carry the `code` the
 * catalogue is keyed on; an `instanceof` check alone left every offline-capable
 * mutation showing the server's English (FF-AUDIT-005).
 */
function envelopeCode(error: unknown): string | undefined {
  if (error instanceof ApiError) return error.code
  const code = (error as { code?: unknown } | null)?.code
  return error instanceof Error && typeof code === 'string' && code ? code : undefined
}

function messageFor(catalog: ReturnType<typeof getCopy<typeof errorCopy.en>>, error: unknown): string {
  const code = envelopeCode(error)
  if (code && error instanceof Error) {
    // The server's English `message` is the LAST resort, not the first: it is
    // precise for a module business rule the catalogue has not covered yet, and
    // replacing it with a generic apology in the right language would trade a
    // translation gap for an information loss.
    return translateErrorCode(code, catalog, error.message) ?? catalog.unknown
  }
  if (error instanceof Error && error.message) return error.message
  return catalog.unknown
}

/** Outside React — mutation callbacks, `lib/` helpers, the MutationCache. */
export function apiErrorMessage(error: unknown): string {
  return messageFor(getCopy(errorCopy, getActiveLocale()), error)
}

/** Inside React. Re-renders when the locale chunk lands, like any other copy. */
export function useApiErrorMessage(): (error: unknown) => string {
  const catalog = useCopy(errorCopy)
  return (error: unknown) => messageFor(catalog, error)
}

export { errorCopy } from './copy'
export type { ErrorCopyCatalog } from './copy'
